import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { cn } from '@/lib/cn'
import { COLOR_LABEL, COLOR_NAMES, LABEL_HEX, type ColorName } from '@/lib/colors'
import { useDeleteLabel, useJobLabelIds, useLabels, useSaveLabel, useToggleJobLabel, type Label } from './queries'

/** The coloured bars above a card's name, as on her Trello cards. */
export function LabelBars({ labels, className }: { labels: Array<Pick<Label, 'id' | 'name' | 'color'>>; className?: string }) {
  if (labels.length === 0) return null
  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {labels.map((label) => (
        <span
          key={label.id}
          title={label.name ?? COLOR_LABEL[label.color]}
          className="h-2 w-10 rounded-full"
          style={{ backgroundColor: LABEL_HEX[label.color] }}
        />
      ))}
    </div>
  )
}

/** Labels with their names, for the wedding page. */
export function LabelChips({ labels }: { labels: Array<Pick<Label, 'id' | 'name' | 'color'>> }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {labels.map((label) => (
        <span
          key={label.id}
          className="inline-flex h-6 min-w-10 items-center rounded px-2 text-[length:var(--text-xs)] font-semibold text-black"
          style={{ backgroundColor: LABEL_HEX[label.color] }}
        >
          {label.name ?? ''}
        </span>
      ))}
    </div>
  )
}

/**
 * The wedding's labels, and a menu to tick labels on and off, create a label
 * (a colour and an optional name), rename, recolour or delete one.
 */
export function JobLabels({ jobId }: { jobId: string }) {
  const labels = useLabels().data ?? []
  const chosen = useJobLabelIds(jobId).data ?? []
  const toggle = useToggleJobLabel(jobId)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Label | 'new' | null>(null)
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClick(event: MouseEvent) {
      if (panel.current && !panel.current.contains(event.target as Node)) {
        setOpen(false)
        setEditing(null)
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        setEditing(null)
      }
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const mine = labels.filter((label) => chosen.includes(label.id))

  return (
    <div className="relative" ref={panel}>
      <p className="label mb-1">Labels</p>
      <div className="flex flex-wrap items-center gap-1.5">
        <LabelChips labels={mine} />
        <Button size="sm" onClick={() => setOpen(!open)} aria-expanded={open}>
          {mine.length ? 'Change' : '+ Add a label'}
        </Button>
      </div>

      {open ? (
        <div className="absolute left-0 z-30 mt-2 w-72 rounded-lg bg-[var(--surface-card)] p-3 shadow-[var(--shadow-menu)]">
          {editing ? (
            <LabelForm label={editing === 'new' ? null : editing} count={labels.length} onDone={() => setEditing(null)} />
          ) : (
            <>
              <p className="mb-2 text-center text-[length:var(--text-sm)] font-semibold">Labels</p>
              <ul className="flex flex-col gap-1.5">
                {labels.map((label) => (
                  <li key={label.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="check size-4 shrink-0"
                      aria-label={label.name ?? COLOR_LABEL[label.color]}
                      checked={chosen.includes(label.id)}
                      onChange={(event) => toggle.mutate({ labelId: label.id, on: event.target.checked })}
                    />
                    <button
                      type="button"
                      onClick={() => toggle.mutate({ labelId: label.id, on: !chosen.includes(label.id) })}
                      className="flex h-8 flex-1 items-center rounded px-2.5 text-left text-[length:var(--text-sm)] font-semibold text-black hover:opacity-85"
                      style={{ backgroundColor: LABEL_HEX[label.color] }}
                    >
                      {label.name ?? ''}
                    </button>
                    <button
                      type="button"
                      aria-label={`Edit ${label.name ?? 'label'}`}
                      onClick={() => setEditing(label)}
                      className="grid size-8 place-items-center rounded-md text-taupe hover:bg-[var(--color-wash-strong)]"
                    >
                      ✎
                    </button>
                  </li>
                ))}
              </ul>
              <Button size="sm" className="mt-3 w-full" onClick={() => setEditing('new')}>
                Create a new label
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}

function LabelForm({ label, count, onDone }: { label: Label | null; count: number; onDone: () => void }) {
  const save = useSaveLabel()
  const remove = useDeleteLabel()
  const [name, setName] = useState(label?.name ?? '')
  const [color, setColor] = useState<ColorName>(label?.color ?? 'green')

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate({ ...(label ? { id: label.id } : { sort_order: count }), name: name.trim() || null, color }, { onSuccess: onDone })
      }}
    >
      <p className="text-center text-[length:var(--text-sm)] font-semibold">{label ? 'Edit label' : 'Create label'}</p>
      <div className="h-8 rounded px-2.5 py-1.5 text-[length:var(--text-sm)] font-semibold text-black" style={{ backgroundColor: LABEL_HEX[color] }}>
        {name}
      </div>
      <Input aria-label="Label name" placeholder="Name (optional)" value={name} onChange={(event) => setName(event.target.value)} />
      <div className="grid grid-cols-5 gap-1.5" role="radiogroup" aria-label="Colour">
        {COLOR_NAMES.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={color === key}
            aria-label={COLOR_LABEL[key]}
            onClick={() => setColor(key)}
            className={cn('h-8 rounded', color === key && 'ring-2 ring-accent ring-offset-2 ring-offset-[var(--surface-card)]')}
            style={{ backgroundColor: LABEL_HEX[key] }}
          />
        ))}
      </div>
      <div className="flex items-center justify-between gap-2">
        <Button type="submit" variant="primary" size="sm" loading={save.isPending}>
          {label ? 'Save' : 'Create'}
        </Button>
        <div className="flex gap-1">
          {label ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                if (window.confirm('Delete this label? It comes off every wedding that has it.')) remove.mutate(label.id, { onSuccess: onDone })
              }}
            >
              Delete
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={onDone}>
            Back
          </Button>
        </div>
      </div>
    </form>
  )
}
